import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BenefitType, Box } from 'edenred-ui';
import { useFormContext } from 'react-hook-form';
import styled, { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';

import { FormFields } from '@epi-forms/helpers';
import {
  required,
  yearlyLimit,
  minAmountMoreAccurate
} from '@epi-helpers/formHelpers/validators';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import { IBenefitGroupSettings } from '@epi-models/pages/Beneficiaries';
import { MIN_ALLOWED_BALANCE } from '@epi-constants/topupForm';

import { FieldInfo } from '../TopupForm.styles';

import { MoneyAmountField } from './MoneyAmountField';
import { WorkingDaysField } from './WorkingDaysField';
import { getMonthNameForLocale, monthDiff } from '@epi-helpers/dateHelper';
import { getMaxWorkingDays } from '@epi-selectors/settings';
import { useOnboardingWizard } from '@epi-shared/hooks';
import { IBenefits } from '@epi-models/pages/BenefitGroups';

const Bold = styled.span`
  font-weight: ${({ theme }) => theme.fontBold};
`;

interface TopupAmountProps {
  benefitType?: IBenefits;
  lowestYearlyLimit: number;
  benefitSettings?: IBenefitGroupSettings;
  remainingBalance: number;
}

export const TopupAmount = ({
  benefitType,
  lowestYearlyLimit,
  benefitSettings,
  remainingBalance
}: TopupAmountProps) => {
  const { t } = useTranslation();
  const { sizeExtraSmall, primaryFrontColor, warningColor } = useTheme();
  const locale = useSelector(getApplicationLanguageId);
  const maxWorkingDays = useSelector(getMaxWorkingDays);
  const {
    formState: { errors },
    watch,
    setValue,
    trigger,
    getValues,
    getFieldState
  } = useFormContext();

  const { isOnBoardingActive } = useOnboardingWizard();

  useEffect(() => {
    setValue(FormFields.MoneyAmount, '');
  }, [benefitType]);

  const scheduleDate = watch(FormFields.ScheduleDate);
  const today = new Date();
  const monthOffset = scheduleDate ? monthDiff(today, scheduleDate) : 0;
  const yearlyLimitRef = useRef(lowestYearlyLimit);
  const hasMountedRef = useRef(false);

  yearlyLimitRef.current = lowestYearlyLimit;

  const dynamicYearlyLimit = useCallback(
    value => yearlyLimit(yearlyLimitRef.current)(value),
    []
  );

  const amountValidators = useMemo(
    () => [required, dynamicYearlyLimit, minAmountMoreAccurate(1)],
    [dynamicYearlyLimit]
  );

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    const amountValue = getValues(FormFields.MoneyAmount);
    const { isTouched, isDirty } = getFieldState(FormFields.MoneyAmount);

    if (amountValue || isTouched || isDirty) {
      trigger(FormFields.MoneyAmount);
    }
  }, [lowestYearlyLimit]);

  return (
    <Box width={500}>
      <Box
        width={275}
        id="deposit_money_topup_step_three"
        display={'flex'}
        flexDirection={'column'}
        gap={2}
        key={benefitType}
      >
        {benefitType === BenefitType.lunch ? (
          <>
            <WorkingDaysField />
            {!errors[FormFields.DaysForLunari] && (
              <FieldInfo id="deposit_money_topup_step_three_info">
                {t('containers.deposit_money.topup_tab.form.days.info')}
              </FieldInfo>
            )}
            <Box
              id="deposit_money_topup_step_three_info"
              color={
                errors[FormFields.DaysForLunari]
                  ? warningColor
                  : primaryFrontColor
              }
              fontSize={sizeExtraSmall}
            >
              <Trans
                i18nKey={t('containers.deposit_money.topup_tab.form.days.max', {
                  month: getMonthNameForLocale(locale, monthOffset),
                  maxDays: maxWorkingDays
                })}
                components={{
                  bold: <Bold />
                }}
              />
            </Box>
          </>
        ) : (
          <MoneyAmountField
            validators={amountValidators}
            labelKey={'containers.deposit_money.topup_tab.form.amount.input'}
          />
        )}
        {remainingBalance < MIN_ALLOWED_BALANCE && !isOnBoardingActive && (
          <Box
            id="deposit_money_topup_step_three_info"
            width={275}
            color={warningColor}
            fontSize={sizeExtraSmall}
          >
            {t('containers.deposit_money.topup_tab.form.payment.over_balance')}
          </Box>
        )}
      </Box>
    </Box>
  );
};
